import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import axios from "axios";

import "./PaymentsAnalyticsDashboard.css";

import { CODEFORGE_BATCH_MASTER } from "../../data/codeForgeBatchMaster";

/* ============================================================
   CONFIGURATION
   ============================================================ */

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "";

const CODEFORGE_PAYMENT_ENDPOINT =
  "get-code-forge-course-payments";

const CODEFORGE_COURSE_ID =
  "6a51d43add03c0ca536c473a";

const BATCH_ORDER = [
  "Advanced",
  "Beginner-B1",
  "Intermediate-B1",
  "Intermediate-B2",
];

/* ============================================================
   HELPERS
   ============================================================ */

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

/* ------------------------------------------------------------
   Normalize Roll Number
   ------------------------------------------------------------ */

function normalizeRollNo(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

/* ------------------------------------------------------------
   Normalize Batch Name
   ------------------------------------------------------------ */

function normalizeBatchName(value) {
  const batch = String(value || "")
    .trim()
    .toLowerCase();

  const batchMap = {
    advanced: "Advanced",

    "beginner-b1": "Beginner-B1",

    "intermediate-b1": "Intermediate-B1",

    "intermediate-b2": "Intermediate-B2",
  };

  return (
    batchMap[batch] ||
    String(value || "").trim()
  );
}

/* ------------------------------------------------------------
   Format Number
   ------------------------------------------------------------ */

function formatNumber(value) {
  const n = Number(value);

  if (
    !Number.isFinite(n) ||
    Number.isNaN(n)
  ) {
    return "0";
  }

  return new Intl.NumberFormat("en-IN").format(n);
}

/* ------------------------------------------------------------
   Format Percentage
   ------------------------------------------------------------ */

function formatPercentage(value) {
  const n = Number(value);

  if (
    !Number.isFinite(n) ||
    Number.isNaN(n)
  ) {
    return "0.00%";
  }

  return `${n.toFixed(2)}%`;
}

/* ------------------------------------------------------------
   CSV Escape
   ------------------------------------------------------------ */

function csvEscape(value) {
  const stringValue = String(value ?? "");

  return /[",\n]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
}

/* ------------------------------------------------------------
   Safe Filename
   ------------------------------------------------------------ */

function sanitizeFilename(value) {
  return String(value || "CodeForge")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/* ============================================================
   BATCH MASTER LOOKUP
   ============================================================ */

function buildBatchLookup() {
  const lookup = new Map();

  safeArray(CODEFORGE_BATCH_MASTER).forEach(
    (student) => {
      /*
       ---------------------------------------------------------
       Support all possible roll-number property names
       ---------------------------------------------------------
      */

      const rawRollNo =
        student?.rollNo ??
        student?.roll_no ??
        student?.RollNo ??
        student?.Rollno ??
        student?.["Roll No"] ??
        student?.["Rollno"] ??
        "";

      const rollNo =
        normalizeRollNo(rawRollNo);

      if (!rollNo) {
        return;
      }

      /*
       ---------------------------------------------------------
       Support all possible batch property names
       ---------------------------------------------------------
      */

      const rawBatch =
        student?.codingBatch ??
        student?.coding_batch ??
        student?.Coding_Batch ??
        student?.["Coding Batch"] ??
        student?.["Coding_Batch"] ??
        "";

      const codingBatch =
        normalizeBatchName(rawBatch);

      /*
       ---------------------------------------------------------
       Support mentor fields
       ---------------------------------------------------------
      */

      const mentor =
        student?.mentor ??
        student?.Mentor ??
        "";

      const poolMentor =
        student?.poolMentor ??
        student?.pool_mentor ??
        student?.["Pool Mentor"] ??
        student?.["pool mentor"] ??
        "";

      /*
       ---------------------------------------------------------
       Store normalized lookup
       ---------------------------------------------------------
      */

      lookup.set(
        rollNo,
        {
          roll_no: rawRollNo,

          name:
            student?.name ??
            student?.Name ??
            "",

          branch:
            student?.branch ??
            student?.Branch ??
            "",

          coding_batch:
            codingBatch,

          mentor,

          pool_mentor:
            poolMentor,

          gender:
            student?.gender ??
            student?.Gender ??
            "",
        }
      );
    }
  );

  return lookup;
}

/* ============================================================
   TERM MULTI SELECT
   ============================================================ */

function TermMultiSelect({
  availableTerms,
  selectedTerms,
  onChange,
  disabled,
}) {
  const [open, setOpen] =
    useState(false);

  const wrapRef =
    useRef(null);

  /* ----------------------------------------------------------
     Close when clicking outside
     ---------------------------------------------------------- */

  useEffect(() => {
    function handleOutside(event) {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    }

    document.addEventListener(
      "mousedown",
      handleOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutside
      );
    };
  }, []);

  /* ----------------------------------------------------------
     Toggle Term
     ---------------------------------------------------------- */

  const toggleTerm = (term) => {
    if (
      selectedTerms.includes(term)
    ) {
      onChange(
        selectedTerms.filter(
          (item) => item !== term
        )
      );

      return;
    }

    onChange([
      ...selectedTerms,
      term,
    ]);
  };

  /* ----------------------------------------------------------
     Select All
     ---------------------------------------------------------- */

  const selectAll = () => {
    onChange(
      availableTerms.slice()
    );
  };

  /* ----------------------------------------------------------
     Clear All
     ---------------------------------------------------------- */

  const clearAll = () => {
    onChange([]);
  };

  /* ----------------------------------------------------------
     Trigger Label
     ---------------------------------------------------------- */

  let triggerLabel =
    "Select Payment Terms";

  if (!availableTerms.length) {
    triggerLabel =
      "No terms available";
  } else if (
    selectedTerms.length === 1
  ) {
    triggerLabel =
      selectedTerms[0];
  } else if (
    selectedTerms.length === 2
  ) {
    triggerLabel =
      selectedTerms.join(" + ");
  } else if (
    selectedTerms.length > 2
  ) {
    triggerLabel =
      `${selectedTerms.length} terms selected`;
  }

  /* ----------------------------------------------------------
     Render
     ---------------------------------------------------------- */

  return (
    <div
      className="cpa-term-select"
      ref={wrapRef}
    >
      <button
        type="button"
        className="cpa-term-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() =>
          setOpen(
            (previous) =>
              !previous
          )
        }
      >
        <span className="cpa-term-trigger-text">
          {triggerLabel}
        </span>

        <span
          className="cpa-chev"
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          className="cpa-term-dropdown"
          role="listbox"
          aria-multiselectable="true"
        >
          {/* ACTIONS */}

          <div className="cpa-term-dropdown-actions">
            <button
              type="button"
              onClick={selectAll}
              disabled={
                !availableTerms.length
              }
            >
              Select All
            </button>

            <button
              type="button"
              onClick={clearAll}
              disabled={
                !selectedTerms.length
              }
            >
              Clear All
            </button>
          </div>

          {/* OPTIONS */}

          <div className="cpa-term-options">
            {!availableTerms.length && (
              <div className="cpa-term-empty">
                No terms available
                for CodeForge.
              </div>
            )}

            {availableTerms.map(
              (term) => (
                <label
                  className="cpa-term-option"
                  key={term}
                >
                  <input
                    type="checkbox"
                    checked={selectedTerms.includes(
                      term
                    )}
                    onChange={() =>
                      toggleTerm(term)
                    }
                  />

                  <span>
                    {term}
                  </span>
                </label>
              )
            )}
          </div>

          {/* FOOTER */}

          <div className="cpa-term-dropdown-footer">
            {selectedTerms.length} term
            {selectedTerms.length === 1
              ? ""
              : "s"}{" "}
            selected
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   SUMMARY CARDS
   ============================================================ */

function SummaryCards({
  overall,
  overallTermSummary,
  selectedTerms,
  isMultipleTerms,
}) {
  const selectedTerm =
    selectedTerms?.[0];

  const singleTermSummary =
    safeArray(
      overallTermSummary
    ).find(
      (term) =>
        term?.term_name ===
        selectedTerm
    ) || {};

  const paidStudents =
    isMultipleTerms
      ? overall?.common_paid_students
      : singleTermSummary?.paid_students;

  const pendingStudents =
    isMultipleTerms
      ? overall?.common_pending_students
      : singleTermSummary?.pending_students;

  const paymentPercentage =
    isMultipleTerms
      ? overall?.payment_percentage
      : singleTermSummary?.payment_percentage;

  return (
    <div className="cpa-summary-cards">

      {/* ------------------------------------------------------
         TOTAL
      ------------------------------------------------------ */}

      <div className="cpa-summary-card">
        <div className="cpa-sc-icon cpa-icon-neutral">
          <span>REG</span>
        </div>

        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            Total Students
          </div>

          <div className="cpa-sc-value">
            {formatNumber(
              overall?.registered_students
            )}
          </div>

          <div className="cpa-sc-desc">
            Valid CodeForge students
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------
         PAID
      ------------------------------------------------------ */}

      <div className="cpa-summary-card cpa-paid cpa-primary-metric">
        <div className="cpa-sc-icon cpa-icon-paid">
          <span>✓</span>
        </div>

        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            {isMultipleTerms
              ? "Common Paid"
              : "Paid"}
          </div>

          <div className="cpa-sc-value">
            {formatNumber(
              paidStudents
            )}
          </div>

          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Paid across all selected terms"
              : `Students paid for ${
                  selectedTerm ||
                  "selected term"
                }`}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------
         PENDING
      ------------------------------------------------------ */}

      <div className="cpa-summary-card cpa-pending">
        <div className="cpa-sc-icon cpa-icon-pending">
          <span>!</span>
        </div>

        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            {isMultipleTerms
              ? "Common Pending"
              : "Pending"}
          </div>

          <div className="cpa-sc-value">
            {formatNumber(
              pendingStudents
            )}
          </div>

          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Not paid across all selected terms"
              : `Not paid for ${
                  selectedTerm ||
                  "selected term"
                }`}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------
         PERCENTAGE
      ------------------------------------------------------ */}

      <div className="cpa-summary-card cpa-pct">
        <div className="cpa-sc-icon cpa-icon-pct">
          <span>%</span>
        </div>

        <div className="cpa-sc-content">
          <div className="cpa-sc-label">
            Payment %
          </div>

          <div className="cpa-sc-value">
            {formatPercentage(
              paymentPercentage
            )}
          </div>

          <div className="cpa-sc-desc">
            {isMultipleTerms
              ? "Common paid / registered"
              : `${
                  selectedTerm ||
                  "Selected term"
                } paid / registered`}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   TERM CARDS
   ============================================================ */

function TermCards({
  termSummary,
}) {
  if (!termSummary.length) {
    return (
      <div className="cpa-term-cards-empty">
        No term-wise data
        available.
      </div>
    );
  }

  return (
    <div className="cpa-term-cards">
      {termSummary.map(
        (term) => {
          const percentage =
            Math.min(
              100,
              Math.max(
                0,
                Number(
                  term.payment_percentage
                ) || 0
              )
            );

          return (
            <div
              className="cpa-term-card"
              key={
                term.term_name
              }
            >
              <div className="cpa-tc-header">
                <div className="cpa-tc-name">
                  {term.term_name ||
                    "—"}
                </div>

                <div className="cpa-tc-percent">
                  {formatPercentage(
                    term.payment_percentage
                  )}
                </div>
              </div>

              <div className="cpa-tc-main">

                <div className="cpa-tc-stat">
                  <span className="cpa-tc-stat-label">
                    Paid
                  </span>

                  <strong className="cpa-tc-paid">
                    {formatNumber(
                      term.paid_students
                    )}
                  </strong>
                </div>

                <div className="cpa-tc-divider" />

                <div className="cpa-tc-stat">
                  <span className="cpa-tc-stat-label">
                    Pending
                  </span>

                  <strong className="cpa-tc-pending">
                    {formatNumber(
                      term.pending_students
                    )}
                  </strong>
                </div>

              </div>

              <div className="cpa-tc-progress">
                <div className="cpa-tc-progress-track">
                  <div
                    className="cpa-tc-progress-fill"
                    style={{
                      width: `${percentage}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
}

/* ============================================================
   BATCH ANALYTICS TABLE
   ============================================================ */

function BatchAnalyticsTable({
  batches,
  terms,
  overall,
  overallTermSummary,
}) {
  const isMultipleTerms = terms.length > 1;
  const selectedTerm = terms[0];

  /* ----------------------------------------------------------
     Get Term Data
     ---------------------------------------------------------- */

  const getTermData = (batch, term) =>
    batch?.term_data?.[term] || {
      registered_students: batch?.registered_students || 0,
      paid_students: 0,
      pending_students: batch?.registered_students || 0,
      payment_percentage: 0,
    };

  /* ----------------------------------------------------------
     Get Overall Term
     ---------------------------------------------------------- */

  const getOverallTerm = (term) =>
    safeArray(overallTermSummary).find(
      (item) => item?.term_name === term
    );

  /* ----------------------------------------------------------
     Common Paid

     Single term:
       Common = selected term.

     Multiple terms:
       Common = students paid in EVERY selected term.
     ---------------------------------------------------------- */

  const getBatchCommonPaid = (batch) => {
    if (!isMultipleTerms) {
      return getTermData(batch, selectedTerm).paid_students;
    }

    return batch.students.filter((student) =>
      terms.every(
        (term) => student.terms?.[term]?.paid === true
      )
    ).length;
  };

  /* ----------------------------------------------------------
     Common Pending
     ---------------------------------------------------------- */

  const getBatchCommonPending = (batch) =>
    Math.max(
      (batch.registered_students || 0) -
        getBatchCommonPaid(batch),
      0
    );

  /* ----------------------------------------------------------
     Common Payment Percentage
     ---------------------------------------------------------- */

  const getBatchCommonPaymentPercentage = (batch) => {
    const registered = batch.registered_students || 0;
    const paid = getBatchCommonPaid(batch);

    return registered > 0
      ? (paid / registered) * 100
      : 0;
  };

  /* ----------------------------------------------------------
     Selected Term Share
     ---------------------------------------------------------- */

  const getBatchTermShare = (batch, term) => {
    const termData = getTermData(batch, term);
    const overallTerm = getOverallTerm(term);

    if (
      !overallTerm ||
      Number(overallTerm.paid_students) <= 0
    ) {
      return 0;
    }

    return (
      (Number(termData.paid_students) /
        Number(overallTerm.paid_students)) *
      100
    );
  };

  /* ----------------------------------------------------------
     Common Share

     Single term:
       selected-term paid / selected-term overall paid.

     Multiple terms:
       common paid / overall common paid.
     ---------------------------------------------------------- */

  const getBatchCommonShare = (batch) => {
    const commonPaid = getBatchCommonPaid(batch);

    if (isMultipleTerms) {
      const overallCommonPaid =
        Number(overall?.common_paid_students) || 0;

      return overallCommonPaid > 0
        ? (commonPaid / overallCommonPaid) * 100
        : 0;
    }

    const overallSelectedTermPaid =
      Number(
        getOverallTerm(selectedTerm)?.paid_students
      ) || 0;

    return overallSelectedTermPaid > 0
      ? (commonPaid / overallSelectedTermPaid) * 100
      : 0;
  };

  /* ----------------------------------------------------------
     Selected-term display helper
     ---------------------------------------------------------- */

  const getSelectedTermOverallData = () =>
    getOverallTerm(selectedTerm) || {};

  return (
    <section className="cpa-section">
      <div className="cpa-section-header">
        <div>
          <div className="cpa-section-kicker">
            Detailed Breakdown
          </div>

          <div className="cpa-section-title">
            Coding Batch Payment Analytics
          </div>
        </div>

        <span className="cpa-tag cpa-tag-muted">
          {terms.length === 1
            ? "Showing selected term"
            : `Showing ${terms.length} selected terms`}
        </span>
      </div>

      <div className="cpa-table-card">
        <div className="cpa-table-scroll">
          <table className="cpa-analytics-table cfa-batch-table">
            <thead>
              <tr className="cpa-group-row">
                <th
                  rowSpan={2}
                  className="cpa-sticky-col cpa-sticky-tech"
                >
                  Coding Batch
                </th>

                <th
                  rowSpan={2}
                  className="cpa-sticky-col cpa-sticky-reg"
                >
                  Registered
                </th>

                <th
                  rowSpan={2}
                  className="cpa-sticky-col cpa-sticky-paid"
                >
                  {isMultipleTerms
                    ? "Common Paid"
                    : "Paid"}
                </th>

                <th
                  rowSpan={2}
                  className="cpa-sticky-col cpa-sticky-pending"
                >
                  {isMultipleTerms
                    ? "Common Pending"
                    : "Pending"}
                </th>

                <th rowSpan={2}>
                  {isMultipleTerms
                    ? "Common Payment %"
                    : "Payment %"}
                </th>

                <th rowSpan={2}>
                  {isMultipleTerms
                    ? "Common Share %"
                    : "Share %"}
                </th>

                {terms.map((term) => (
                  <th
                    key={term}
                    colSpan={4}
                    className="cpa-term-group-header"
                  >
                    {term}
                  </th>
                ))}
              </tr>

              <tr className="cpa-sub-header">
                {terms.map((term) => (
                  <React.Fragment key={`${term}-headers`}>
                    <th>Paid</th>
                    <th>Pending</th>
                    <th>Payment %</th>
                    <th>Share %</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ==================================================
                  OVERALL
                  ================================================== */}

              <tr className="cpa-overall-row">
                <td className="cpa-sticky-col cpa-sticky-tech">
                  <div className="cpa-overall-label">
                    <span className="cpa-overall-dot" />
                    Overall
                  </div>
                </td>

                <td className="cpa-sticky-col cpa-sticky-reg">
                  {formatNumber(
                    overall?.registered_students
                  )}
                </td>

                <td className="cpa-sticky-col cpa-sticky-paid cpa-cell-paid cpa-strong">
                  {formatNumber(
                    isMultipleTerms
                      ? overall?.common_paid_students
                      : getSelectedTermOverallData()
                          ?.paid_students
                  )}
                </td>

                <td className="cpa-sticky-col cpa-sticky-pending cpa-cell-pending">
                  {formatNumber(
                    isMultipleTerms
                      ? overall?.common_pending_students
                      : getSelectedTermOverallData()
                          ?.pending_students
                  )}
                </td>

                <td className="cpa-cell-pct cpa-strong">
                  {formatPercentage(
                    isMultipleTerms
                      ? overall?.payment_percentage
                      : getSelectedTermOverallData()
                          ?.payment_percentage
                  )}
                </td>

                <td className="cpa-cell-dim">
                  100.00%
                </td>

                {terms.map((term) => {
                  const data = getOverallTerm(term);

                  return (
                    <React.Fragment key={`overall-${term}`}>
                      <td className="cpa-cell-paid">
                        {formatNumber(
                          data?.paid_students
                        )}
                      </td>

                      <td className="cpa-cell-pending">
                        {formatNumber(
                          data?.pending_students
                        )}
                      </td>

                      <td className="cpa-cell-pct">
                        {formatPercentage(
                          data?.payment_percentage
                        )}
                      </td>

                      <td>
                        {formatPercentage(
                          data?.share_percentage ?? 100
                        )}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ==================================================
                  BATCHES
                  ================================================== */}

              {batches.map((batch) => {
                const registered =
                  batch.registered_students || 0;

                const commonPaid =
                  getBatchCommonPaid(batch);

                const commonPending =
                  getBatchCommonPending(batch);

                const commonPaymentPercentage =
                  getBatchCommonPaymentPercentage(batch);

                return (
                  <tr
                    key={batch.batch_name}
                    className={
                      batch.batch_name === "Unmatched"
                        ? "cpa-unmatched-row"
                        : ""
                    }
                  >
                    <td className="cpa-tech-cell cpa-sticky-col cpa-sticky-tech">
                      <div className="cpa-batch-name">
                        <span className="cpa-batch-dot" />
                        <span className="cpa-tech-name">
                          {batch.batch_name}
                        </span>
                      </div>
                    </td>

                    <td className="cpa-sticky-col cpa-sticky-reg">
                      {formatNumber(registered)}
                    </td>

                    <td className="cpa-sticky-col cpa-sticky-paid cpa-cell-paid cpa-strong">
                      {formatNumber(commonPaid)}
                    </td>

                    <td className="cpa-sticky-col cpa-sticky-pending cpa-cell-pending">
                      {formatNumber(commonPending)}
                    </td>

                    <td className="cpa-cell-pct">
                      <div className="cpa-tech-payment">
                        <span>
                          {formatPercentage(
                            commonPaymentPercentage
                          )}
                        </span>

                        <div className="cpa-mini-progress">
                          <div
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(
                                  0,
                                  commonPaymentPercentage
                                )
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    <td>
                      {batch.batch_name === "Unmatched"
                        ? "—"
                        : formatPercentage(
                            getBatchCommonShare(batch)
                          )}
                    </td>

                    {terms.map((term) => {
                      const data = getTermData(
                        batch,
                        term
                      );

                      return (
                        <React.Fragment
                          key={`${batch.batch_name}-${term}`}
                        >
                          <td className="cpa-cell-paid">
                            {formatNumber(
                              data.paid_students
                            )}
                          </td>

                          <td className="cpa-cell-pending">
                            {formatNumber(
                              data.pending_students
                            )}
                          </td>

                          <td className="cpa-cell-pct">
                            <div className="cpa-tech-payment">
                              <span>
                                {formatPercentage(
                                  data.payment_percentage
                                )}
                              </span>

                              <div className="cpa-mini-progress">
                                <div
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      Math.max(
                                        0,
                                        Number(
                                          data.payment_percentage
                                        ) || 0
                                      )
                                    )}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </td>

                          <td>
                            {batch.batch_name === "Unmatched"
                              ? "—"
                              : formatPercentage(
                                  getBatchTermShare(
                                    batch,
                                    term
                                  )
                                )}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="cpa-table-footnote">
          <span className="cpa-info-dot">i</span>
          <span>
            {isMultipleTerms
              ? "Common Paid represents students with valid payment in every selected term. Each term group shows the individual selected-term result."
              : `The fixed columns show the selected term (${selectedTerm || "—"}), and the ${selectedTerm || "selected term"} group provides the same term-level breakdown.`}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function CodeForgePaymentAnalytics() {

  /* ----------------------------------------------------------
     STATE
     ---------------------------------------------------------- */

  const [
    courseData,
    setCourseData,
  ] = useState(null);

  const [
    selectedTerms,
    setSelectedTerms,
  ] = useState([]);

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    lastUpdated,
    setLastUpdated,
  ] = useState(null);

  /* ----------------------------------------------------------
     BATCH LOOKUP
     ---------------------------------------------------------- */

  const batchLookup =
    useMemo(
      () =>
        buildBatchLookup(),
      []
    );

  /* ----------------------------------------------------------
     FETCH CODEFORGE PAYMENT DATA
     ---------------------------------------------------------- */

  const fetchCodeForgePayments =
    useCallback(
      async () => {

        setIsLoading(true);

        setError("");

        try {

          const response =
            await axios.post(
              `${API_BASE_URL}${CODEFORGE_PAYMENT_ENDPOINT}`,
              {
                course_id:
                  CODEFORGE_COURSE_ID,
              }
            );

          const result =
            response.data;

          if (
            !result ||
            result.success !== true
          ) {
            throw new Error(
              result?.message ||
                "Unable to load CodeForge payment analytics."
            );
          }

          setCourseData(
            result
          );

          /*
           ----------------------------------------------------
           Backend terms
           ----------------------------------------------------
          */

          const backendTerms =
            safeArray(
              result.selected_terms
            );

          /*
           ----------------------------------------------------
           Default: select the first available term
           ----------------------------------------------------
          */

          setSelectedTerms((previous) => {
            if (previous.length) {
              const validPrevious = backendTerms.filter((term) =>
                previous.includes(term)
              );

              return validPrevious.length
                ? validPrevious
                : backendTerms.length
                  ? [backendTerms[0]]
                  : [];
            }

            return backendTerms.length
              ? [backendTerms[0]]
              : [];
          });

          setLastUpdated(
            new Date()
          );

        } catch (
          requestError
        ) {

          console.error(
            "CodeForge payment analytics error:",
            requestError
          );

          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
              requestError?.message ||
              "Unable to load CodeForge payment analytics."
          );

          setCourseData(null);

        } finally {

          setIsLoading(false);

        }
      },
      []
    );

  /* ----------------------------------------------------------
     Initial API Call
     ---------------------------------------------------------- */

  useEffect(() => {
    fetchCodeForgePayments();
  }, [
    fetchCodeForgePayments,
  ]);

  /* ==========================================================
     RAW STUDENTS
     ========================================================== */

  const students =
    safeArray(
      courseData?.students
    );

  const availableTerms =
    safeArray(
      courseData?.selected_terms
    );

  /* ==========================================================
     ENRICH STUDENTS WITH BATCH MASTER
     ========================================================== */

  const studentsWithBatch =
    useMemo(() => {

      return students.map(
        (student) => {

          /*
           ----------------------------------------------------
           Backend roll number
           ----------------------------------------------------
          */

          const normalizedRoll =
            normalizeRollNo(
              student?.roll_no ||
                student?.temp_roll_no
            );

          /*
           ----------------------------------------------------
           Match against static batch master
           ----------------------------------------------------
          */

          const batchInfo =
            batchLookup.get(
              normalizedRoll
            ) || null;

          /*
           ----------------------------------------------------
           Return enriched student
           ----------------------------------------------------
          */

          return {
            ...student,

            normalized_roll:
              normalizedRoll,

            batch_info:
              batchInfo,

            batch:
              normalizeBatchName(
                batchInfo?.coding_batch
              ),
          };
        }
      );

    }, [
      students,
      batchLookup,
    ]);

  /* ==========================================================
     BATCH GROUPING
     ========================================================== */

  const batchAnalytics =
    useMemo(() => {

      const result = {};

      /*
       --------------------------------------------------------
       Initialize known batches
       --------------------------------------------------------
      */

      BATCH_ORDER.forEach(
        (batchName) => {

          result[batchName] = {
            batch_name:
              batchName,

            students: [],

            registered_students:
              0,

            term_data: {},
          };

        }
      );

      /*
       --------------------------------------------------------
       Unmatched
       --------------------------------------------------------
      */

      result.Unmatched = {
        batch_name:
          "Unmatched",

        students: [],

        registered_students:
          0,

        term_data: {},
      };

      /*
       --------------------------------------------------------
       Group students
       --------------------------------------------------------
      */

      studentsWithBatch.forEach(
        (student) => {

          const normalizedBatch =
            normalizeBatchName(
              student?.batch
            );

          const batchName =
            normalizedBatch &&
            result[
              normalizedBatch
            ]
              ? normalizedBatch
              : "Unmatched";

          result[
            batchName
          ].students.push(
            student
          );

          result[
            batchName
          ].registered_students +=
            1;
        }
      );

      /*
       --------------------------------------------------------
       Calculate term data
       --------------------------------------------------------
      */

      Object.values(
        result
      ).forEach(
        (batch) => {

          availableTerms.forEach(
            (term) => {

              const paidStudents =
                batch.students.filter(
                  (student) =>
                    student.terms?.[
                      term
                    ]?.paid === true
                );

              const registered =
                batch.students.length;

              const paid =
                paidStudents.length;

              const pending =
                Math.max(
                  registered -
                    paid,
                  0
                );

              const paymentPercentage =
                registered > 0
                  ? (paid /
                      registered) *
                    100
                  : 0;

              batch.term_data[
                term
              ] = {

                registered_students:
                  registered,

                paid_students:
                  paid,

                pending_students:
                  pending,

                payment_percentage:
                  paymentPercentage,
              };
            }
          );
        }
      );

      return result;

    }, [
      studentsWithBatch,
      availableTerms,
    ]);

  /* ==========================================================
     DISPLAYED BATCHES
     ========================================================== */

  const displayedBatches =
    useMemo(() => {

      return [
        ...BATCH_ORDER,
        "Unmatched",
      ]
        .map(
          (batchName) =>
            batchAnalytics[
              batchName
            ]
        )
        .filter(Boolean);

    }, [
      batchAnalytics,
    ]);

  /* ==========================================================
     OVERALL TERM SUMMARY
     ========================================================== */

  const overallTermSummary =
    useMemo(() => {

      return selectedTerms.map(
        (term) => {

          const registered =
            studentsWithBatch.length;

          const paid =
            studentsWithBatch.filter(
              (student) =>
                student.terms?.[
                  term
                ]?.paid === true
            ).length;

          const pending =
            Math.max(
              registered -
                paid,
              0
            );

          const paymentPercentage =
            registered > 0
              ? (paid /
                  registered) *
                100
              : 0;

          return {
            term_name:
              term,

            registered_students:
              registered,

            paid_students:
              paid,

            pending_students:
              pending,

            payment_percentage:
              paymentPercentage,
          };
        }
      );

    }, [
      studentsWithBatch,
      selectedTerms,
    ]);

  /* ==========================================================
     OVERALL SUMMARY
     ========================================================== */

  const overall =
    useMemo(() => {

      const registered =
        studentsWithBatch.length;

      /*
       --------------------------------------------------------
       No term selected
       --------------------------------------------------------
      */

      if (
        selectedTerms.length ===
        0
      ) {
        return {
          registered_students:
            registered,

          paid_students:
            0,

          pending_students:
            registered,

          payment_percentage:
            0,
        };
      }

      /*
       --------------------------------------------------------
       Single term
       --------------------------------------------------------
      */

      if (
        selectedTerms.length ===
        1
      ) {

        const selectedTerm =
          selectedTerms[0];

        const summary =
          overallTermSummary.find(
            (term) =>
              term.term_name ===
              selectedTerm
          );

        return {
          registered_students:
            registered,

          paid_students:
            summary?.paid_students ||
            0,

          pending_students:
            summary?.pending_students ||
            0,

          payment_percentage:
            summary?.payment_percentage ||
            0,
        };
      }

      /*
       --------------------------------------------------------
       Multiple terms
       --------------------------------------------------------
      */

      const commonPaid =
        studentsWithBatch.filter(
          (student) =>
            selectedTerms.every(
              (term) =>
                student.terms?.[
                  term
                ]?.paid === true
            )
        ).length;

      const commonPending =
        Math.max(
          registered -
            commonPaid,
          0
        );

      const paymentPercentage =
        registered > 0
          ? (commonPaid /
              registered) *
            100
          : 0;

      return {
        registered_students:
          registered,

        common_paid_students:
          commonPaid,

        common_pending_students:
          commonPending,

        payment_percentage:
          paymentPercentage,
      };

    }, [
      studentsWithBatch,
      selectedTerms,
      overallTermSummary,
    ]);

  /* ==========================================================
     TERM CHANGE
     ========================================================== */

  const handleTermsChange =
    (nextTerms) => {

      setSelectedTerms(
        nextTerms
      );

    };

  /* ==========================================================
     EXPORT CSV
     ========================================================== */

  const handleExportClick =
    () => {

      if (!courseData) {
        return;
      }

      const terms =
        selectedTerms.length
          ? selectedTerms
          : availableTerms;

      const rows = [];

      /*
       --------------------------------------------------------
       Headers
       --------------------------------------------------------
      */

      const headers = [
        "Coding Batch",
        "Registered",
        isMultipleTerms ? "Common Paid" : "Paid",
        isMultipleTerms ? "Common Pending" : "Pending",
        isMultipleTerms ? "Common Payment %" : "Payment %",
        isMultipleTerms ? "Common Share %" : "Share %",
      ];

      terms.forEach(
        (term) => {

          headers.push(
            `${term} Paid`,
            `${term} Pending`,
            `${term} Payment %`,
            `${term} Share %`
          );

        }
      );

      rows.push(headers);

      /*
       --------------------------------------------------------
       Batch rows
       --------------------------------------------------------
      */

      displayedBatches.forEach(
        (batch) => {

          const registered =
            batch.registered_students ||
            0;

          const commonPaid =
            terms.length > 1
              ? batch.students.filter(
                  (student) =>
                    terms.every(
                      (term) =>
                        student
                          .terms?.[
                          term
                        ]?.paid === true
                    )
                ).length
              : batch
                  .term_data?.[
                  terms[0]
                ]
                  ?.paid_students ||
                0;

          const commonPending =
            Math.max(
              registered -
                commonPaid,
              0
            );

          const paymentPercentage =
            registered > 0
              ? (commonPaid /
                  registered) *
                100
              : 0;

          const commonShare =
            overallTermSummary.find((item) =>
              item?.term_name === terms[0]
            )?.paid_students > 0
              ? (commonPaid /
                  (isMultipleTerms
                    ? overall?.common_paid_students || 0
                    : overallTermSummary.find((item) =>
                        item?.term_name === terms[0]
                      )?.paid_students || 0)) *
                100
              : 0;

          const row = [
            batch.batch_name,
            registered,
            commonPaid,
            commonPending,
            formatPercentage(paymentPercentage),
            batch.batch_name === "Unmatched"
              ? "—"
              : formatPercentage(commonShare),
          ];

          /*
           ------------------------------------------------------
           Term columns
           ------------------------------------------------------
          */

          terms.forEach(
            (term) => {

              const termData =
                batch.term_data?.[
                  term
                ] || {};

              const overallTerm =
                overallTermSummary.find(
                  (item) =>
                    item.term_name ===
                    term
                );

              const share =
                overallTerm?.paid_students >
                0
                  ? (
                      (
                        termData.paid_students ||
                        0
                      ) /
                      overallTerm.paid_students
                    ) *
                    100
                  : 0;

              row.push(

                termData.paid_students ||
                  0,

                termData.pending_students ||
                  0,

                formatPercentage(
                  termData.payment_percentage
                ),

                formatPercentage(
                  share
                )
              );

            }
          );

          rows.push(row);

        }
      );

      /*
       --------------------------------------------------------
       Build CSV
       --------------------------------------------------------
      */

      const csvContent =
        rows
          .map(
            (row) =>
              row
                .map(csvEscape)
                .join(",")
          )
          .join("\n");

      /*
       --------------------------------------------------------
       Filename
       --------------------------------------------------------
      */

      const termsSafe =
        terms
          .map(
            sanitizeFilename
          )
          .join("-");

      const filename =
        `CodeForge-${
          termsSafe ||
          "All-Terms"
        }-Payment-Analytics.csv`;

      /*
       --------------------------------------------------------
       Download
       --------------------------------------------------------
      */

      const blob =
        new Blob(
          [csvContent],
          {
            type:
              "text/csv;charset=utf-8;",
          }
        );

      const url =
        URL.createObjectURL(
          blob
        );

      const anchor =
        document.createElement(
          "a"
        );

      anchor.href = url;

      anchor.download =
        filename;

      document.body.appendChild(
        anchor
      );

      anchor.click();

      document.body.removeChild(
        anchor
      );

      URL.revokeObjectURL(
        url
      );
    };

  /* ==========================================================
     LOADING
     ========================================================== */

  if (isLoading) {

    return (
      <div className="cpa-app">

        <div className="cpa-skeleton-wrap">

          <div className="cpa-sk-cards">

            <div className="cpa-sk-block cpa-sk-card" />

            <div className="cpa-sk-block cpa-sk-card" />

            <div className="cpa-sk-block cpa-sk-card" />

            <div className="cpa-sk-block cpa-sk-card" />

          </div>

          <div className="cpa-sk-term-row">

            <div className="cpa-sk-block cpa-sk-term" />

            <div className="cpa-sk-block cpa-sk-term" />

          </div>

          <div className="cpa-sk-block cpa-sk-table" />

        </div>

      </div>
    );
  }

  /* ==========================================================
     ERROR
     ========================================================== */

  if (
    error &&
    !courseData
  ) {

    return (
      <div className="cpa-app">

        <div className="cpa-banner cpa-banner-error">

          <span className="cpa-banner-icon">
            !
          </span>

          <span>
            {error}
          </span>

          <button
            type="button"
            className="cpa-btn cpa-btn-secondary"
            onClick={
              fetchCodeForgePayments
            }
          >
            Retry
          </button>

        </div>

      </div>
    );
  }

  /* ==========================================================
     TERM MODE
     ========================================================== */

  const isMultipleTerms =
    selectedTerms.length > 1;

  /* ==========================================================
     MAIN UI
     ========================================================== */

  return (
    <div className="cpa-app">

      {/* ======================================================
          HEADER
      ====================================================== */}

      <header className="cpa-masthead">

        <div className="cpa-brand-block">

          <div className="cpa-eyebrow">
            PAYMENT REPORTING
          </div>

          <h1>
            CodeForge Payment
            Analytics
          </h1>

          <p className="cpa-sub">
            CodeForge batch-wise
            payment completion
            overview
          </p>

        </div>

        {courseData && (
          <div className="cpa-course-context">

            <div className="cpa-context-label">
              SELECTED COURSE
            </div>

            <div className="cpa-cname">
              {courseData.course_name ||
                "CODEFORGE"}
            </div>

            <div className="cpa-cmeta">
              {selectedTerms.length
                ? selectedTerms.join(
                    " + "
                  )
                : "—"}
            </div>

            {courseData.program_name && (
              <div className="cpa-cid">
                {
                  courseData.program_name
                }
              </div>
            )}

          </div>
        )}

      </header>

      {/* ======================================================
          CONTROL PANEL
      ====================================================== */}

      <section className="cpa-panel">

        <div className="cpa-panel-header">

          <div>

            <div className="cpa-panel-title">
              Analytics
              Configuration
            </div>

            <div className="cpa-panel-subtitle">
              Choose the payment
              terms you want to
              review.
            </div>

          </div>

          {selectedTerms.length >
            0 && (
            <div className="cpa-selection-count">

              {selectedTerms.length}{" "}
              term
              {selectedTerms.length ===
              1
                ? ""
                : "s"}{" "}
              selected

            </div>
          )}

        </div>

        <div className="cpa-panel-grid">

          {/* COURSE */}

          <div className="cpa-field cpa-course-field">

            <label>
              Course
            </label>

            <div className="cpa-control cpa-static-control">

              {courseData?.course_name ||
                "CODEFORGE"}

            </div>

          </div>

          {/* TERMS */}

          <div className="cpa-field cpa-terms-field">

            <label>
              Payment Terms
            </label>

            <TermMultiSelect
              availableTerms={
                availableTerms
              }
              selectedTerms={
                selectedTerms
              }
              onChange={
                handleTermsChange
              }
              disabled={
                !availableTerms.length
              }
            />

          </div>

          {/* ACTIONS */}

          <div className="cpa-actions">

            <button
              type="button"
              className="cpa-btn cpa-btn-primary"
              disabled={
                !selectedTerms.length
              }
              onClick={
                fetchCodeForgePayments
              }
            >
              <span>
                ↻
              </span>

              <span>
                Generate
                Analytics
              </span>
            </button>

            <button
              type="button"
              className="cpa-btn cpa-btn-secondary"
              onClick={
                fetchCodeForgePayments
              }
            >
              <span className="cpa-refresh-icon">
                ↻
              </span>

              <span className="cpa-refresh-text">
                Refresh
              </span>
            </button>

            <button
              type="button"
              className="cpa-btn cpa-btn-ghost"
              disabled={
                !courseData
              }
              onClick={
                handleExportClick
              }
            >
              <span>
                ⇩
              </span>

              <span>
                Export CSV
              </span>
            </button>

          </div>

        </div>

        {error && (
          <div className="cpa-validation-msg">

            <span className="cpa-banner-icon">
              !
            </span>

            <span>
              {error}
            </span>

          </div>
        )}

      </section>

      {/* ======================================================
          DASHBOARD
      ====================================================== */}

      {courseData &&
        selectedTerms.length >
          0 && (

        <main className="cpa-dashboard-body">

          {/* --------------------------------------------------
              DASHBOARD CONTEXT
          -------------------------------------------------- */}

          <div className="cpa-dashboard-context">

            <div>

              <div className="cpa-dashboard-course">
                {courseData.course_name ||
                  "CODEFORGE"}
              </div>

              <div className="cpa-dashboard-term-line">
                Selected terms:

                <span>
                  {selectedTerms.join(
                    " + "
                  )}
                </span>
              </div>

            </div>

            <div className="cpa-dashboard-meta">

              {lastUpdated && (
                <>
                  Last updated:{" "}
                  {lastUpdated.toLocaleString(
                    "en-IN"
                  )}
                </>
              )}

            </div>

          </div>

          {/* --------------------------------------------------
              SUMMARY
          -------------------------------------------------- */}

          <SummaryCards
            overall={overall}
            overallTermSummary={
              overallTermSummary
            }
            selectedTerms={
              selectedTerms
            }
            isMultipleTerms={
              isMultipleTerms
            }
          />

          {/* --------------------------------------------------
              TERM-WISE
          -------------------------------------------------- */}

          <section className="cpa-section">

            <div className="cpa-section-header">

              <div>

                <div className="cpa-section-kicker">
                  Individual Terms
                </div>

                <div className="cpa-section-title">
                  Term-Wise Payment
                  Status
                </div>

              </div>

              <span className="cpa-tag cpa-tag-muted">
                Individual term
                results
              </span>

            </div>

            <TermCards
              termSummary={
                overallTermSummary
              }
            />

          </section>

          {/* --------------------------------------------------
              BATCH TABLE
          -------------------------------------------------- */}

          <BatchAnalyticsTable
            batches={
              displayedBatches
            }
            terms={
              selectedTerms
            }
            overall={
              overall
            }
            overallTermSummary={
              overallTermSummary
            }
          />

          {/* --------------------------------------------------
              UNMATCHED WARNING
          -------------------------------------------------- */}

          {batchAnalytics
            .Unmatched
            ?.students
            ?.length > 0 && (

            <div className="cpa-banner cpa-banner-warning">

              <span className="cpa-banner-icon">
                !
              </span>

              <span>

                {formatNumber(
                  batchAnalytics
                    .Unmatched
                    .students.length
                )}{" "}

                student(s) could
                not be matched
                with the static
                CodeForge batch
                master and are
                shown under
                Unmatched.

              </span>

            </div>
          )}

          {/* --------------------------------------------------
              FOOTER
          -------------------------------------------------- */}

          <div className="cpa-meta-bar">

            <span>
              Payment status is
              taken from the
              backend. Static
              batch master data
              is used only to
              identify the
              student's coding
              batch.
            </span>

            <span>
              CodeForge Payment
              Analytics
            </span>

          </div>

        </main>
      )}

      {/* ======================================================
          EMPTY
      ====================================================== */}

      {!courseData &&
        !error && (

        <div className="cpa-empty-state">

          <div className="cpa-es-icon">
            <span>
              ⌁
            </span>
          </div>

          <div className="cpa-es-title">
            CodeForge analytics
            unavailable.
          </div>

          <div className="cpa-es-sub">
            Please refresh and
            try again.
          </div>

        </div>
      )}

    </div>
  );
}